import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Settings as SettingsIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  WarningAmber as WarningAmberIcon,
  Star as StarIcon,
  Restore as RestoreIcon,
  CloudQueue as CloudIcon,
  List as ListIcon,
  Add as AddIcon,
  Memory as ModelIcon,
  ViewInAr as VectorSizeIcon,
  Layers as BatchSizeIcon,
  InfoOutlined as DescriptionIcon,
} from '@mui/icons-material';
import {
  Box,
  Alert,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Card,
  CardContent,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { ConfigDetailRow } from '../components/ui/ConfigDetailRow';
import { PageLayout } from '../components/ui/PageLayout';
import {
  AdminTabs,
  type AdminTab,
} from '../components/admin';
import { EmbeddingConfigEditDialog, EmbeddingConfigCard } from '../components/EmbeddingConfig';
import { useSnackbar } from '../contexts/SnackbarContext';

import { EmbeddingTestResponse, EmbeddingProviderType } from '../api/ApiClient';
import type { EmbeddingConfig } from '../api/ApiClient';
import { 
  useGetEmbeddingConfigs, 
  useGetCurrentEmbeddingConfig, 
  useGetEmbeddingProviders,
  useSaveEmbeddingConfig,
  useTestEmbeddingConfig,
  useDeleteEmbeddingConfig,
  useActivateEmbeddingConfig,
  useResetToEnvEmbeddingConfig
} from '@/hooks/api/config/useEmbeddingConfig';
import { log } from '../utils/errors/logger';

interface EmbeddingProvider {
  value: string;
  label: string;
  description: string;
  default_model: string;
  default_vector_size: number;
  required_config: string[];
  optional_config: string[];
  is_active: boolean;
}

export default function EmbeddingConfig() {
  const { t } = useTranslation();
  const { showSuccess, showError } = useSnackbar();
  const [tabValue, setTabValue] = useState(0);
  
  // TanStack Query hooks
  const { data: providers = [], isLoading: providersLoading } = useGetEmbeddingProviders();
  const { data: configs = [], isLoading: configsLoading } = useGetEmbeddingConfigs();
  const { data: currentConfig, isLoading: currentConfigLoading } = useGetCurrentEmbeddingConfig();
  
  // Mutation hooks
  const saveConfigMutation = useSaveEmbeddingConfig();
  const testConfigMutation = useTestEmbeddingConfig();
  const deleteConfigMutation = useDeleteEmbeddingConfig();
  const activateConfigMutation = useActivateEmbeddingConfig();
  const resetToEnvMutation = useResetToEnvEmbeddingConfig();
  
  // Derived loading state (exclude activate mutation as it has its own dialog loading)
  const loading = providersLoading || configsLoading || currentConfigLoading || 
                 saveConfigMutation.isPending || testConfigMutation.isPending || 
                 deleteConfigMutation.isPending || resetToEnvMutation.isPending;
  
  // Dialog states
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [activateDialogOpen, setActivateDialogOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<Partial<EmbeddingConfig> | null>(null);
  const [configToDelete, setConfigToDelete] = useState<string | null>(null);
  const [configToActivate, setConfigToActivate] = useState<EmbeddingConfig | null>(null);
  const [testResult, setTestResult] = useState<EmbeddingTestResponse | null>(null);

  const toEmbeddingRequest = (config: Partial<EmbeddingConfig>): EmbeddingConfig | null => {
    if (!config.provider || !config.model || !config.vector_size) {
      return null;
    }

    return {
      provider: config.provider as EmbeddingProviderType,
      model: config.model,
      vector_size: config.vector_size,
      batch_size: config.batch_size ?? 100,
      timeout: config.timeout ?? 30,
      max_retries: config.max_retries ?? 3,
      provider_config: config.provider_config ?? {},
      display_name: config.display_name ?? '',
      description: config.description ?? '',
    };
  };

  const handleTestConfig = (config: Partial<EmbeddingConfig>) => {
    log.user.action('embedding_config_test_initiated', 'EmbeddingConfig', { model: config.model });
    const request = toEmbeddingRequest(config);
    if (!request) {
      showError(t('embedding.messages.saveFailed'));
      return;
    }

    testConfigMutation.mutate(request, {
      onSuccess: (result) => {
        setTestResult(result);
        setTestDialogOpen(true);
        log.user.action('embedding_config_test_completed', 'EmbeddingConfig', {
          success: result.success,
          model: config.model
        });
      },
      onError: () => {
        showError(t('embedding.messages.testFailed'));
      }
    });
  };

  const handleSaveConfig = (config: Partial<EmbeddingConfig>) => {
    log.user.action('embedding_config_save_initiated', 'EmbeddingConfig', { model: config.model });
    const request = toEmbeddingRequest(config);
    if (!request) {
      showError(t('embedding.messages.saveFailed'));
      return;
    }

    saveConfigMutation.mutate(request, {
      onSuccess: () => {
        showSuccess(t('embedding.messages.saveSuccess'));
        setConfigDialogOpen(false);
        log.user.action('embedding_config_saved', 'EmbeddingConfig', { model: config.model });
      },
      onError: () => {
        showError(t('embedding.messages.saveFailed'));
      }
    });
  };

  const handleDeleteConfig = (configId: string) => {
    log.user.action('embedding_config_delete_initiated', 'EmbeddingConfig', { configId });
    deleteConfigMutation.mutate(configId, {
      onSuccess: () => {
        showSuccess(t('embedding.messages.deleteSuccess'));
        setDeleteDialogOpen(false);
        log.user.action('embedding_config_deleted', 'EmbeddingConfig', { configId });
      },
      onError: () => {
        showError(t('embedding.messages.deleteFailed'));
      }
    });
  };

  const handleActivateConfig = (configId: string) => {
    log.user.action('embedding_config_activate_initiated', 'EmbeddingConfig', { configId });

    // Close dialog immediately for better UX
    setActivateDialogOpen(false);
    setConfigToActivate(null);

    activateConfigMutation.mutate(configId, {
      onSuccess: () => {
        showSuccess(t('embedding.messages.activateSuccess'));
        log.user.action('embedding_config_activated', 'EmbeddingConfig', { configId });
      },
      onError: (error) => {
        log.api.error('POST', '/api/admin/embedding-config/activate', error);
        showError(t('embedding.messages.activateFailed'));
      }
    });
  };

  const handleResetToEnv = () => {
    log.user.action('embedding_config_reset_to_env_initiated', 'EmbeddingConfig');
    resetToEnvMutation.mutate(undefined, {
      onSuccess: () => {
        showSuccess(t('embedding.messages.resetSuccess'));
        log.user.action('embedding_config_reset_to_env_completed', 'EmbeddingConfig');
      },
      onError: () => {
        showError(t('embedding.messages.resetFailed'));
      }
    });
  };

  const openEditDialog = (config?: EmbeddingConfig) => {
    if (config) {
      setEditingConfig({ ...config });
    } else {
      const defaultProvider = providers[0];
      setEditingConfig({
        provider: (defaultProvider?.value || 'openai') as EmbeddingProviderType,
        model: defaultProvider?.default_model || 'text-embedding-ada-002',
        vector_size: defaultProvider?.default_vector_size || 1536,
        batch_size: 100,
        timeout: 30,
        max_retries: 3,
        provider_config: {},
        display_name: '',
        description: '',
        is_active: false,
      });
    }
    setConfigDialogOpen(true);
  };

  // Define tabs with standardized structure
  const tabs: AdminTab[] = [
    {
      label: t('embedding.tabs.currentConfiguration'),
      icon: <SettingsIcon />,
      content: (
        <>
          {currentConfig && (
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent sx={{ p: { xs: 2, md: 3 }, '&:last-child': { pb: { xs: 2, md: 3 } } }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: { xs: 'stretch', md: 'flex-start' },
                    gap: { xs: 2, md: 3 },
                  }}
                >
                  {/* LEFT: title + active badge */}
                  <Box sx={{ width: { xs: '100%', md: 200 }, flexShrink: 0 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, lineHeight: 1.3 }}>
                      {currentConfig.display_name || t('embedding.tabs.currentConfiguration')}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                      <Chip
                        label={t('embedding.currentConfig.active')}
                        color="success"
                        size="small"
                        icon={<CheckIcon />}
                      />
                    </Box>
                  </Box>

                  {/* Vertical divider — md+ only */}
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ display: { xs: 'none', md: 'block' } }}
                  />

                  {/* MIDDLE: detail rows */}
                  <Box sx={{ flexGrow: 1 }}>
                    <Stack spacing={1.25}>
                      <ConfigDetailRow
                        icon={<CloudIcon fontSize="small" />}
                        label={t('embedding.currentConfig.provider')}
                        value={currentConfig.provider}
                      />
                      <ConfigDetailRow
                        icon={<ModelIcon fontSize="small" />}
                        label={t('embedding.currentConfig.model')}
                        value={currentConfig.model}
                      />
                      <ConfigDetailRow
                        icon={<VectorSizeIcon fontSize="small" />}
                        label={t('embedding.currentConfig.vectorSize')}
                        value={currentConfig.vector_size}
                      />
                      <ConfigDetailRow
                        icon={<BatchSizeIcon fontSize="small" />}
                        label={t('embedding.currentConfig.batchSize')}
                        value={currentConfig.batch_size}
                      />
                      {currentConfig.description && (
                        <ConfigDetailRow
                          icon={<DescriptionIcon fontSize="small" />}
                          label={t('embedding.currentConfig.description')}
                          value={currentConfig.description}
                          alignItems="flex-start"
                        />
                      )}
                    </Stack>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}
        </>
      ),
    },
    {
      label: t('embedding.tabs.allConfigurations'),
      icon: <ListIcon />,
      content: (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">{t('embedding.tabs.allConfigurations')}</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                startIcon={<AddIcon />}
                variant="contained"
                onClick={() => openEditDialog()}
              >
                {t('embedding.actions.addConfiguration')}
              </Button>
              <Button 
                startIcon={<RestoreIcon />}
                variant="outlined"
                onClick={handleResetToEnv}
                disabled={resetToEnvMutation.isPending}
              >
                {t('embedding.actions.resetToEnvironment')}
              </Button>
            </Box>
          </Box>
          
          <Stack spacing={2}>
            {configs.map((config, index) => (
              <EmbeddingConfigCard
                key={config.id ?? index}
                config={config}
                onTest={handleTestConfig}
                onResetToEnv={handleResetToEnv}
                onSetToActivate={(cfg) => {
                  setConfigToActivate(cfg);
                  setActivateDialogOpen(true);
                }}
                onEdit={openEditDialog}
                onDelete={(configId) => {
                  setConfigToDelete(configId);
                  setDeleteDialogOpen(true);
                }}
              />
            ))}
          </Stack>
        </>
      ),
    },
    {
      label: t('embedding.tabs.availableProviders'),
      icon: <CloudIcon />,
      content: (
        <>
          <Typography variant="h6" gutterBottom>{t('embedding.providers.title')}</Typography>
          <Grid container spacing={2}>
            {providers.map((provider) => (
              <Grid size={{ xs: 12, md: 6 }} key={provider.value}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="h6">{provider.label}</Typography>
                      <Chip
                        label={provider.is_active ? t('embedding.currentConfig.active') : t('embedding.currentConfig.inactive')}
                        color={provider.is_active ? "success" : "default"}
                        icon={provider.is_active ? <CheckIcon /> : <WarningIcon />}
                        size="small"
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {provider.description}
                    </Typography>
                    <Typography variant="body2">
                      {t('embedding.providers.defaultModel')}: {provider.default_model}
                    </Typography>
                    <Typography variant="body2">
                      {t('embedding.providers.defaultVectorSize')}: {provider.default_vector_size}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {t('embedding.providers.requiredConfig')}: {provider.required_config.join(', ')}
                    </Typography>
                    {provider.optional_config.length > 0 && (
                      <Typography variant="body2">
                        {t('embedding.providers.optionalConfig')}: {provider.optional_config.join(', ')}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      ),
    },
  ];

  return (
    <>
      <PageLayout
        title={t('embedding.title')}
        isLoading={configsLoading || currentConfigLoading || providersLoading}
        maxWidth="xl"
      >
        <AdminTabs
          tabs={tabs}
          value={tabValue}
          onChange={setTabValue}
          variant="standard"
        />
      </PageLayout>

      {/* Configuration Edit Dialog */}
      <EmbeddingConfigEditDialog
        open={configDialogOpen}
        config={editingConfig}
        providers={providers}
        onClose={() => setConfigDialogOpen(false)}
        onSave={handleSaveConfig}
        onTest={handleTestConfig}
      />

      {/* Test Result Dialog */}
      <Dialog open={testDialogOpen} onClose={() => setTestDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t('embedding.dialog.testResultTitle')}</DialogTitle>
        <DialogContent>
          {testResult && (
            <Box>
              <Alert severity={testResult.success ? 'success' : 'error'} sx={{ mb: 2 }}>
                {testResult.message}
              </Alert>
              {testResult.test_results && (
                <Box>
                  <Typography variant="h6" gutterBottom>{t('embedding.testResult.providerInformation')}</Typography>
                  <pre>{JSON.stringify(testResult.test_results, null, 2)}</pre>
                </Box>
              )}
              {testResult.error && (
                <Box>
                  <Typography variant="h6" gutterBottom>{t('embedding.testResult.errorDetails')}</Typography>
                  <Typography variant="body2" color="error">
                    {testResult.error}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDialogOpen(false)}>{t('embedding.dialog.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('embedding.dialog.deleteTitle')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('embedding.dialog.deleteMessage')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('embedding.dialog.cancel')}</Button>
          <Button
            onClick={() => configToDelete && handleDeleteConfig(configToDelete)}
            color="error"
          >
            {t('embedding.dialog.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Activate Confirmation Dialog */}
      <Dialog 
        open={activateDialogOpen} 
        onClose={() => !activateConfigMutation.isPending && setActivateDialogOpen(false)} 
        maxWidth="md"
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon sx={{ color: 'warning.main' }} />
          {t('embedding.dialog.activateTitle')}
        </DialogTitle>
        <DialogContent>
          {configToActivate && (
            <Box>
              <Typography gutterBottom>
                {t('embedding.dialog.activateWarning')}
              </Typography>
              <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1, mb: 2 }}>
                <Typography variant="body2">
                  <strong>{t('embedding.currentConfig.provider')}:</strong> {configToActivate.provider}
                </Typography>
                <Typography variant="body2">
                  <strong>{t('embedding.currentConfig.model')}:</strong> {configToActivate.model}
                </Typography>
                <Typography variant="body2">
                  <strong>{t('embedding.currentConfig.vectorSize')}:</strong> {configToActivate.vector_size}
                </Typography>
              </Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>{t('embedding.dialog.activateImportant')}</strong>
                </Typography>
                <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                  {(t('embedding.dialog.activateConsequences', { returnObjects: true }) as string[]).map((consequence: string, index: number) => (
                    <li key={index}>{consequence}</li>
                  ))}
                </ul>
              </Alert>
              <Typography variant="body2">
                {t('embedding.dialog.activateConfirm')}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setActivateDialogOpen(false)}
            disabled={activateConfigMutation.isPending}
          >
            {t('embedding.dialog.cancel')}
          </Button>
          <Button 
            onClick={() => configToActivate && handleActivateConfig(configToActivate.id!)} 
            color="warning"
            variant="contained"
            disabled={activateConfigMutation.isPending}
            startIcon={activateConfigMutation.isPending ? <CircularProgress size={20} /> : <StarIcon />}
          >
            {activateConfigMutation.isPending ? t('embedding.dialog.activating') : t('embedding.dialog.activateButton')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading Spinner */}
      {loading && (
        <Box sx={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 9999
        }}>
          <CircularProgress />
        </Box>
      )}
    </>
  );
}
